import { useQuery } from "@tanstack/react-query";
import React from "react";
import type { ApiClient } from "../../../app/api/client";
import { CheckInActionCode } from "../../../app/checkin/checkInActionCode";
import { pickCheckInSessionSource } from "../../../app/checkin/checkInSource";
import type { PersonReaderActionEvent } from "./PersonReaderActionsTable";
import { personDetailQueryKeys } from "./personDetailQueryKeys";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const PERCENT_SCALE = 100;
const DATE_PART_WIDTH = 2;
/** Der Trendvergleich stellt den aktuellen dem gleich langen Vorzeitraum gegenüber. */
const TREND_PERIOD_COUNT = 2;

/**
 * Check-ins einer Person für die Blöcke der Übersicht (Kennzahlen, Verlauf, Sitzungen, Lesegerät-Aktionen):
 * eine Abfrage, daraus Sitzungen (Ein-/Auschecken gepaart) und Nebenaktionen. Der Schlüssel liegt unter
 * `person.overview`, damit Invalidierungen der Übersicht ihn mitnehmen.
 */

export type AttendanceSessionRow = {
  id: string;
  date: string;
  checkInTime: string | null;
  checkOutTime: string | null;
  duration: number | null;
  studioId: string | null;
  source: string | number | null;
  accessMedium: string | null;
  readerDeviceName: string | null;
  readerEndpointName: string | null;
  readerEndpointIdentifier: string | null;
  allowed: boolean;
  message: string | null;
  isComplete: boolean;
};

const CHECKIN_ACTION_IN = CheckInActionCode.CheckIn;
const CHECKIN_ACTION_OUT = CheckInActionCode.CheckOut;
const READER_ACTION_MIN = CheckInActionCode.OpenDoor;
const READER_ACTION_MAX = CheckInActionCode.DeductPoints;
export const ATTENDANCE_CHART_DAYS = 30;
export const DAYS_PER_MONTH_TO_WEEK_FACTOR = 4.29;
const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_ATTENDANCE_SESSION_MINUTES = 24 * 60;

/** Rohe Check-in-Zeile aus der API; die Gruppierung darunter erzeugt die Anzeigeform. */
export type CheckInApiRow = {
  id: string;
  createdAt: string;
  action: number;
  studioId?: string | null;
  source?: string | null;
  message?: string | null;
  allowed?: boolean;
  walletId?: string | null;
  walletLabel?: string | null;
  studioName?: string | null;
  accessMedium?: string | null;
  readerDeviceName?: string | null;
  readerEndpointName?: string | null;
  readerEndpointIdentifier?: string | null;
  /** Weitere Felder je Ereignisart; hier nicht ausgewertet. */
  [key: string]: unknown;
};

export function groupCheckIns(list: CheckInApiRow[]): AttendanceSessionRow[] {
  const sorted = [...list].sort((a, b) => {
    const timeDiff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    if (timeDiff !== 0) return timeDiff;

    const aAction = Number(a.action);
    const bAction = Number(b.action);
    if (aAction !== bAction) {
      if (aAction === CHECKIN_ACTION_OUT) return -1;
      if (bAction === CHECKIN_ACTION_OUT) return 1;
      if (aAction === CHECKIN_ACTION_IN) return 1;
      if (bAction === CHECKIN_ACTION_IN) return -1;
    }

    return String(b.id ?? "").localeCompare(String(a.id ?? ""));
  });
  const sessions: AttendanceSessionRow[] = [];
  const processed = new Set<string>();

  for (let i = 0; i < sorted.length; i++) {
    const item = sorted[i];
    if (processed.has(item.id)) continue;

    const actionNum = Number(item.action);
    if (actionNum !== CHECKIN_ACTION_IN && actionNum !== CHECKIN_ACTION_OUT) {
      processed.add(item.id);
      continue;
    }

    if (item.action === CHECKIN_ACTION_OUT) {
      let checkIn: CheckInApiRow | null = null;
      const checkOutTime = new Date(item.createdAt);
      for (let j = i + 1; j < sorted.length; j++) {
        const prev = sorted[j];
        const checkInTime = new Date(prev.createdAt);
        const durationMinutes = Math.round((checkOutTime.getTime() - checkInTime.getTime()) / MS_PER_MINUTE);
        if (
          prev.action === CHECKIN_ACTION_IN
          && prev.studioId === item.studioId
          && !processed.has(prev.id)
          && durationMinutes >= 0
          && durationMinutes <= MAX_ATTENDANCE_SESSION_MINUTES
        ) {
          checkIn = prev;
          break;
        }
      }

      if (checkIn) {
        processed.add(item.id);
        processed.add(checkIn.id);
        const checkInTime = new Date(checkIn.createdAt);
        const duration = checkOutTime.getTime() - checkInTime.getTime();
        const durationMinutes = Math.round(duration / MS_PER_MINUTE);

        sessions.push({
          id: checkIn.id,
          date: checkIn.createdAt,
          checkInTime: checkIn.createdAt,
          checkOutTime: item.createdAt,
          duration: durationMinutes,
          studioId: checkIn.studioId ?? null,
          source: pickCheckInSessionSource(checkIn.source ?? null, item.source ?? null) ?? null,
          accessMedium: checkIn.accessMedium || item.accessMedium || null,
          readerDeviceName: checkIn.readerDeviceName || item.readerDeviceName || null,
          readerEndpointName: checkIn.readerEndpointName || item.readerEndpointName || null,
          readerEndpointIdentifier: checkIn.readerEndpointIdentifier || item.readerEndpointIdentifier || null,
          allowed: checkIn.allowed === true && item.allowed === true,
          message: checkIn.message || item.message || null,
          isComplete: true
        });
      } else {
        processed.add(item.id);
        sessions.push({
          id: item.id,
          date: item.createdAt,
          checkInTime: null,
          checkOutTime: item.createdAt,
          duration: null,
          studioId: item.studioId ?? null,
          source: item.source ?? null,
          accessMedium: item.accessMedium || null,
          readerDeviceName: item.readerDeviceName || null,
          readerEndpointName: item.readerEndpointName || null,
          readerEndpointIdentifier: item.readerEndpointIdentifier || null,
          allowed: item.allowed === true,
          message: item.message ?? null,
          isComplete: false
        });
      }
    } else if (item.action === CHECKIN_ACTION_IN) {
      processed.add(item.id);
      sessions.push({
        id: item.id,
        date: item.createdAt,
        checkInTime: item.createdAt,
        checkOutTime: null,
        duration: null,
        studioId: item.studioId ?? null,
        source: item.source ?? null,
        accessMedium: item.accessMedium || null,
        readerDeviceName: item.readerDeviceName || null,
        readerEndpointName: item.readerEndpointName || null,
        readerEndpointIdentifier: item.readerEndpointIdentifier || null,
        allowed: item.allowed === true,
        message: item.message ?? null,
        isComplete: false
      });
    }
  }

  return sessions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function calculateAttendanceStats(sessions: AttendanceSessionRow[]) {
  const now = new Date();
  const last30Days = sessions.filter((s) => now.getTime() - new Date(s.date).getTime() <= ATTENDANCE_CHART_DAYS * DAY_MS);
  const last7Days = sessions.filter((s) => now.getTime() - new Date(s.date).getTime() <= DAYS_PER_WEEK * DAY_MS);

  const avgDuration = (list: AttendanceSessionRow[]) => {
    const withDuration = list.filter((s) => s.duration != null);
    if (withDuration.length === 0) return 0;
    const total = withDuration.reduce((sum, s) => sum + Number(s.duration || 0), 0);
    return Math.round(total / withDuration.length);
  };

  return {
    total: sessions.length,
    last30Days: last30Days.length,
    last7Days: last7Days.length,
    avgDuration30: avgDuration(last30Days),
    avgDuration7: avgDuration(last7Days)
  };
}

export function calculateAttendanceTimeSeries(sessions: Array<{ date: string }>) {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - ATTENDANCE_CHART_DAYS);

  const dateMap = new Map<string, { date: string; count: number; totalMinutes: number }>();
  for (let i = 0; i < ATTENDANCE_CHART_DAYS; i++) {
    const date = new Date(start);
    date.setDate(date.getDate() + i);
    const dateStr = date.toISOString().slice(0, ISO_DATE_LENGTH);
    dateMap.set(dateStr, { date: dateStr, count: 0, totalMinutes: 0 });
  }

  sessions.forEach((s) => {
    const dateStr = String(s.date || "").slice(0, ISO_DATE_LENGTH);
    const entry = dateMap.get(dateStr);
    if (entry) entry.count++;
  });

  return Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));
}

export const ATTENDANCE_HEATMAP_WEEKS = 13;
const DAYS_PER_WEEK = 7;

/** Kalendertag in Ortszeit als `YYYY-MM-DD` — ein Abendbesuch zählt zu seinem Tag, nicht zum UTC-Tag. */
function localDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(DATE_PART_WIDTH, "0");
  const day = String(date.getDate()).padStart(DATE_PART_WIDTH, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export type AttendanceHeatmapDay = { date: string; count: number; future: boolean };

/**
 * Besuche je Tag der letzten {@link ATTENDANCE_HEATMAP_WEEKS} Kalenderwochen (Montag bis Sonntag), Woche für
 * Woche; Tage nach heute sind als `future` markiert und bleiben leer.
 */
export function calculateAttendanceHeatmap(sessions: Array<{ date: string }>, today = new Date()): AttendanceHeatmapDay[][] {
  const counts = new Map<string, number>();
  for (const session of sessions) {
    const date = new Date(session.date);
    if (Number.isNaN(date.getTime())) continue;
    const key = localDateKey(date);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const mondayOffset = (start.getDay() + DAYS_PER_WEEK - 1) % DAYS_PER_WEEK;
  start.setDate(start.getDate() - mondayOffset - (ATTENDANCE_HEATMAP_WEEKS - 1) * DAYS_PER_WEEK);
  const todayKey = localDateKey(today);
  const weeks: AttendanceHeatmapDay[][] = [];
  for (let week = 0; week < ATTENDANCE_HEATMAP_WEEKS; week++) {
    const days: AttendanceHeatmapDay[] = [];
    for (let weekday = 0; weekday < DAYS_PER_WEEK; weekday++) {
      const date = new Date(start);
      date.setDate(start.getDate() + week * DAYS_PER_WEEK + weekday);
      const key = localDateKey(date);
      days.push({ date: key, count: counts.get(key) ?? 0, future: key > todayKey });
    }
    weeks.push(days);
  }
  return weeks;
}

/** Veränderung der Besuche der letzten 30 Tage gegenüber den 30 Tagen davor in Prozent; ohne Vergleichsbasis `null`. */
export function calculateAttendanceTrendPercent(sessions: Array<{ date: string }>, now = new Date()): number | null {
  let current = 0;
  let previous = 0;
  for (const session of sessions) {
    const age = now.getTime() - new Date(session.date).getTime();
    if (age < 0) continue;
    if (age <= ATTENDANCE_CHART_DAYS * DAY_MS) current++;
    else if (age <= TREND_PERIOD_COUNT * ATTENDANCE_CHART_DAYS * DAY_MS) previous++;
  }
  return previous > 0 ? Math.round(((current - previous) / previous) * PERCENT_SCALE) : null;
}

export function formatAttendanceDuration(minutes: number) {
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  const mins = minutes % MINUTES_PER_HOUR;
  if (hours > 0) return `${hours}h ${mins}min`;
  return `${mins}min`;
}

export function usePersonCheckIns(api: Pick<ApiClient, "get">, personId: string, enabled: boolean) {
  const query = useQuery({
    queryKey: [...personDetailQueryKeys.person.overview(personId), "checkins"],
    enabled: enabled && Boolean(personId),
    retry: false,
    queryFn: () => api.get<CheckInApiRow[]>(`/api/v1/checkins?personId=${encodeURIComponent(personId)}&includeAllActions=true`)
  });
  const data = React.useMemo(() => {
    const raw = [...(query.data || [])];
    const attendanceRaw = raw.filter((x) => Number(x.action) === CHECKIN_ACTION_IN || Number(x.action) === CHECKIN_ACTION_OUT);
    const readerEvents: PersonReaderActionEvent[] = raw
      .filter((x) => Number(x.action) >= READER_ACTION_MIN && Number(x.action) <= READER_ACTION_MAX)
      .sort((a, b) => new Date(String(b.createdAt)).getTime() - new Date(String(a.createdAt)).getTime())
      .map((x) => ({
        id: String(x.id),
        createdAt: String(x.createdAt),
        action: Number(x.action),
        studioId: x.studioId != null ? String(x.studioId) : null,
        source: x.source != null ? String(x.source) : null,
        message: x.message != null ? String(x.message) : null,
        allowed: !!x.allowed,
        walletId: x.walletId != null ? String(x.walletId) : null,
        walletLabel: x.walletLabel != null ? String(x.walletLabel) : null
      }));
    const sessions = groupCheckIns(attendanceRaw);
    return {
      sessions,
      readerEvents,
      stats: calculateAttendanceStats(sessions),
      timeSeries: calculateAttendanceTimeSeries(sessions)
    };
  }, [query.data]);
  return { ...data, isPending: enabled && query.isPending, isError: query.isError };
}
